import { UploadOutlined } from "@ant-design/icons";
import {
  Checkbox,
  DatePicker,
  Image,
  Input,
  message,
  Select,
  Upload,
} from "antd";
import { Country } from "country-state-city";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { useHistory, useLocation } from "react-router-dom";
import Router from "../";
import logoMobile from "../../../src/images/SurfingCre1.png";
import logo from "../../../src/images/SurfingCre2.png";
import { fileToBase64 } from "../utils/fileToBase64";
import { cleanPhoneNumber, validateField, validators } from "../utils/Regex";
import { CATEGORY_MAPPING } from "../utils/categories";

export const SignUp = () => {
  // ✅ antd message 로컬 인스턴스
  const [messageApi, contextHolder] = message.useMessage();
  const history = useHistory();
  const location = useLocation();

  // 상태 관리
  const [authType, setAuthType] = useState("signup");
  const [signupType, setSignupType] = useState(false);
  const [signupFields, setSignupFields] = useState({
    email: "",
    password: "",
    passwordCheck: "",
    first_name: "",
    last_name: "",
    phone: "",
    country: "",
    street_address: "",
    address_line: "",
    city: "",
    state_province: "",
    postal_code: "",
    marketing_agreement: false,
    private_agreement: false,
    terms_agreement: false,
    // influencer 전용 필드
    birth: "",
    gender: "",
    nickname: "",
    // Brand 전용 필드
    company_name: "",
    industry: "",
    registration_number: "",
    // 소셜 로그인 필드
    oauth_id: null,
  });
  const isKorea = signupFields.country === "KR";
  const [emailCheckStatus, setEmailCheckStatus] = useState({
    checked: false,
    available: false,
    message: "",
  });
  const [emailVerification, setEmailVerification] = useState({
    code: "",
    verified: false,
    sent: false,
    countdown: 0,
    serverCode: "",
  });

  const [registrationNumberCheckStatus, setRegistrationNumberCheckStatus] =
    useState({
      available: false,
      message: "",
    });

  const [validationStatus, setValidationStatus] = useState({
    email: { isValid: false, message: "" },
    password: { isValid: false, message: "" },
    passwordCheck: { isValid: false, message: "" },
    first_name: { isValid: false, message: "" },
    last_name: { isValid: false, message: "" },
    phone: { isValid: false, message: "" },
    company_name: { isValid: false, message: "" },
    registration_number: { isValid: false, message: "" },
    private_agreement: { isValid: false, message: "" },
  });
  const [invitedLogs, setInvitedLogs] = useState([]); // 초대받은 회사 목록
  const [selectedCompanyId, setSelectedCompanyId] = useState(null); // 선택된 회사 ID
  const filteredInvitedLogs = invitedLogs.filter(
    (log) => log.invite_type === signupType
  );

  const searchParams = new URLSearchParams(location.search);
  const socialParam = searchParams.get("social");
  const typeParam = searchParams.get("type");

  useEffect(() => {
    if (!socialParam && typeParam) {
      const signupType =
        typeParam === "brand"
          ? "brand"
          : typeParam === "agency"
            ? "agency"
            : "influencer";
      setSignupType(signupType);
    }

    // 구글 소셜 로그인
    if (socialParam === "google") {
      const socialSignupData = sessionStorage.getItem("socialSignup");
      if (socialSignupData) {
        try {
          const data = JSON.parse(socialSignupData);

          setSignupFields((prev) => ({
            ...prev,
            email: data.email || "",
            first_name: data.first_name || "",
            last_name: data.last_name || "",
            oauth_id: data.oauth_id || null,
          }));

          setSignupType("influencer");
        } catch (e) {
          console.error("소셜 데이터 파싱 오류", e);
        }
      }
    }
  }, [socialParam, typeParam, location.search]);

  // 사업자등록증 업로드
  const [registrationImg, setRegistrationImg] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  // 폼 초기화 함수
  const resetSignupForm = () => {
    setSignupFields({
      email: "",
      password: "",
      passwordCheck: "",
      first_name: "",
      last_name: "",
      phone: "",
      country: "",
      street_address: "",
      address_line: "",
      city: "",
      state_province: "",
      postal_code: "",
      marketing_agreement: false,
      private_agreement: false,
      terms_agreement: false,
      birth: "",
      gender: "",
      nickname: "",
      company_name: "",
      industry: "",
      registration_number: "",
      oauth_id: null,
    });
    setEmailCheckStatus({
      checked: false,
      available: false,
      message: "",
    });
    setEmailVerification({
      code: "",
      verified: false,
      sent: false,
      countdown: 0,
      serverCode: "",
    });
    setRegistrationNumberCheckStatus({
      available: false,
      message: "",
    });
    setValidationStatus({
      email: { isValid: false, message: "" },
      password: { isValid: false, message: "" },
      passwordCheck: { isValid: false, message: "" },
      first_name: { isValid: false, message: "" },
      last_name: { isValid: false, message: "" },
      phone: { isValid: false, message: "" },
      company_name: { isValid: false, message: "" },
      registration_number: { isValid: false, message: "" },
      private_agreement: { isValid: false, message: "" },
    });
    setInvitedLogs([]);
    setSelectedCompanyId(null);
    setRegistrationImg(null);
    setSelectedFile(null);
  };

  // authType, 소셜 로그인 여부 폼 초기화
  useEffect(() => {
    if (socialParam) return;

    if (authType === "signup") resetSignupForm();
    if (authType === "findauth") resetSignupForm();
  }, [authType, socialParam]);

  // === 입력 핸들러들 ===
  const handleSignupChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    setSignupFields((prev) => ({ ...prev, [name]: newValue }));

    if (name === "email") {
      setEmailCheckStatus({ checked: false, available: false, message: "" });
      setEmailVerification({
        code: "",
        verified: false,
        sent: false,
        countdown: 0,
        serverCode: "",
      });
    }

    const validationFields = [
      "email",
      "password",
      "passwordCheck",
      "first_name",
      "last_name",
      "phone",
      "company_name",
      "private_agreement",
      "terms_agreement",
    ];
    if (validationFields.includes(name)) {
      const validation = validateField(name, newValue, {
        password: signupFields.password,
      });
      setValidationStatus((prev) => ({ ...prev, [name]: validation }));
    }
  };

  // === API 관련 함수들 ===
  const validateAndCheckEmail = async (email, type = "signup") => {
    if (!email) {
      messageApi.warning("이메일을 입력해주세요.");
      return false;
    }
    if (!validators.email(email)) {
      messageApi.error("올바른 이메일 형식을 입력해주세요.");
      return false;
    }
    return true;
  };

  const handleEmailCheck = async () => {
    if (!(await validateAndCheckEmail(signupFields.email))) return;
    const data = await Router.Api.checkEmail({ email: signupFields.email });
    if (!data) return;
    const available = !!data?.result;
    const invitedLog = data?.result?.invited_log ?? [];
    const messageText = data?.error?.text ?? "";

    setInvitedLogs(invitedLog);
    setEmailCheckStatus({ checked: true, available, message: messageText });

    if (available) {
      setEmailVerification((prev) => ({
        ...prev,
        sent: true,
        countdown: 300,
        serverCode: data?.result?.authCode ?? "",
        verified: false,
      }));
      messageApi.info("인증번호가 발송되었습니다.");
      const timer = setInterval(() => {
        setEmailVerification((prev) =>
          prev.countdown <= 1
            ? (clearInterval(timer), { ...prev, countdown: 0 })
            : { ...prev, countdown: prev.countdown - 1 }
        );
      }, 1000);
    } else if (messageText) {
      messageApi.error(messageText);
    }
  };

  const handleVerificationCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6); // 숫자만, 6자리 제한
    setEmailVerification((prev) => ({ ...prev, code: value }));
  };

  const handleFileSelect = async (file) => {
    const base64 = await fileToBase64(file);
    setRegistrationImg(base64);
    setSelectedFile(file);
    return false;
  };

  const handleVerifyEmailCode = () => {
    if (!emailVerification.code || emailVerification.code.length !== 6) {
      messageApi.warning("6자리 인증번호를 입력해주세요.");
      return;
    }

    if (emailVerification.code === emailVerification.serverCode) {
      setEmailVerification((prev) => ({ ...prev, verified: true }));
      messageApi.success("이메일 인증이 완료되었습니다.");
    } else {
      messageApi.error("인증번호가 올바르지 않습니다.");
    }
  };

  // === 제출 핸들러들 ===
  const onSignupSubmit = async (e) => {
    e.preventDefault();

    // 이메일 중복 체크 및 인증 검증
    if (!emailCheckStatus.checked || !emailCheckStatus.available) {
      messageApi.warning("이메일 중복 체크를 완료해주세요.");
      return;
    }
    if (!emailVerification.verified) {
      messageApi.warning("이메일 인증을 완료해주세요.");
      return;
    }

    if (!socialParam) {
      if (!validators.password(signupFields.password)) {
        messageApi.error("올바른 비밀번호 형식을 입력해주세요.");
        return;
      }
      if (signupFields.password !== signupFields.passwordCheck) {
        messageApi.error("비밀번호가 일치하지 않습니다.");
        return;
      }
    }

    if (!signupFields.phone) {
      messageApi.warning("전화번호를 입력해주세요.");
      return;
    }
    if (!validators.phone(signupFields.phone)) {
      messageApi.error(
        "올바른 전화번호 형식을 입력해주세요. 예: +821012345678"
      );
      return;
    }
    if (!signupFields.country) {
      messageApi.warning("국가를 선택해주세요.");
      return;
    }
    if (
      (signupType === "brand" || signupType === "agency") &&
      !selectedCompanyId
    ) {
      if (!signupFields.industry) {
        messageApi.warning("업종을 선택해주세요.");
        return;
      }
    }
    if (
      !signupFields.address_line ||
      !signupFields.address_line.trim() ||
      !signupFields.street_address ||
      !signupFields.street_address.trim()
    ) {
      messageApi.warning("주소를 입력해주세요.");
      return;
    }

    if (
      (signupType === "brand" || signupType === "agency") &&
      !selectedCompanyId
    ) {
      if (!signupFields.registration_number) {
        messageApi.warning("사업자등록번호를 입력해주세요.");
        return;
      }
      if (isKorea && !registrationNumberCheckStatus?.available) {
        messageApi.warning("사업자등록번호를 인증해주세요.");
        return;
      }
      if (!registrationImg) {
        messageApi.warning(
          "사업자 등록증 또는 W-9/Invoice 이미지를 업로드해주세요."
        );
        return;
      }
    }

    if (signupType === "influencer") {
      if (!signupFields.gender) {
        messageApi.warning("성별을 선택해주세요.");
        return;
      }
    }

    if (!signupFields.private_agreement) {
      messageApi.warning("개인정보처리방침 동의는 필수입니다.");
      return;
    }
    if (!signupFields.terms_agreement) {
      messageApi.warning("서비스 이용 약관 동의는 필수입니다.");
      return;
    }

    // 검증 완료 확인
    const isFormValid =
      validators.email(signupFields.email) &&
      (socialParam ||
        (validators.password(signupFields.password) &&
          signupFields.password === signupFields.passwordCheck)) &&
      (signupType !== "influencer" ||
        (!validators.jamo(signupFields.first_name) &&
          validators.name(signupFields.first_name))) &&
      (signupType !== "influencer" ||
        (!validators.jamo(signupFields.last_name) &&
          validators.lastName(signupFields.last_name))) &&
      validators.phone(signupFields.phone) &&
      !!signupFields.country &&
      !!signupFields.street_address?.trim() &&
      !!signupFields.address_line?.trim() &&
      !!signupFields.city?.trim() &&
      !!signupFields.state_province?.trim() &&
      !!signupFields.postal_code?.trim() &&
      signupFields.private_agreement &&
      signupFields.terms_agreement &&
      ((signupType !== "brand" && signupType !== "agency") ||
        selectedCompanyId ||
        (!validators.jamo(signupFields.company_name) &&
          !!signupFields.company_name?.trim())) &&
      (signupType !== "influencer" ||
        (!!signupFields.birth?.trim() &&
          !!signupFields.gender &&
          !!signupFields.nickname?.trim()));

    if (!isFormValid) {
      messageApi.error("모든 항목을 올바르게 입력해주세요.");
      return;
    }

    // payload 구성 (API 스펙 준수)
    const commonPayload = {
      email: signupFields.email,
      oauth_id: socialParam ? signupFields.oauth_id : null, // 소셜 로그인일 때만 oauth_id 포함
      last_name: signupFields.last_name,
      first_name: signupFields.first_name,
      app_key: socialParam
        ? `geumbada${signupFields.oauth_id}` // 소셜 로그인: geumbada+oauth_id 형식
        : signupFields.password,
      phone: cleanPhoneNumber(signupFields.phone), // 전화번호 정리
      country: signupFields.country,
      street_address: signupFields.street_address,
      address_line: signupFields.address_line,
      city: signupFields.city,
      state_province: signupFields.state_province,
      postal_code: signupFields.postal_code,
      platform: socialParam ? "google" : "own", // 소셜 로그인: google, 일반: own
      private_agreement: signupFields.private_agreement,
      terms_agreement: signupFields.terms_agreement,
      marketing_agreement: signupFields.marketing_agreement,
    };

    let payload = {};
    if (signupType === "brand" || signupType === "agency") {
      payload = {
        ...commonPayload,
        user_type: signupType, // "brand" 또는 "agency"
        company_name: signupFields.company_name,
        industry: signupFields.industry,
        registration_number: signupFields.registration_number,
        company_id: selectedCompanyId,
        registration_img: registrationImg,
      };
    } else {
      payload = {
        ...commonPayload,
        user_type: "influencer",
        birth: signupFields.birth,
        nickname: signupFields.nickname,
        membership: null,
        gender: signupFields.gender,
        company_id: selectedCompanyId, // 인플루언서도 초대받은 회사가 있으면 company_id 전달
      };
    }

    const data = await Router.Api.signUp(payload);
    if (data?.result) {
      // 소셜 로그인 데이터 정리
      if (socialParam) {
        sessionStorage.removeItem("socialSignup");
      }
      history.replace(`/signin?type=${signupType}`);
    }
  };

  const handleRegistrationNumberCheck = async () => {
    if (!signupFields.registration_number) {
      messageApi.warning("사업자등록번호를 입력해주세요.");
      return;
    }

    const result = await Router.Api.validateBusinessNumber({
      b_no: signupFields.registration_number,
    });
    if (result) {
      return setRegistrationNumberCheckStatus({
        available: true,
        message: "인증이 완료되었습니다.",
      });
    } else {
      return setRegistrationNumberCheckStatus({
        available: false,
        message: "사업자등록번호 인증에 실패했습니다.",
      });
    }
  };

  return (
    <div className="auth flex flex-col md:flex-row relative">
      {/* ✅ antd message 컨텍스트 렌더링 (반드시 포함) */}
      {contextHolder}

      <div className="absolute top-4 left-4 z-10">
        <button className="w-14 h-14 flex items-center justify-center">
          <Router.LanguageTranslator iconColor="black" />
        </button>
      </div>

      {authType === "signup" && (
        <>
          <div
            className={`login_box w-full md:w-[45%] md:shadow-[8px_0px_8px_0_rgba(50,42,42,0.7)] ${
              !signupType ? "justify-center" : "justify-start"
            }`}
          >
            {!signupType && (
              <img
                src={logoMobile}
                className="block md:hidden mb-12 mx-auto w-1/2"
                alt="로고"
              />
            )}
            <img src={logo} className="logo hidden md:block" alt="로고" />
            {signupType && (
              <>
                <h1>
                  {signupType === "brand"
                    ? "브랜드"
                    : signupType === "agency"
                      ? "에이전시"
                      : "크리에이터"}{" "}
                  회원가입
                </h1>
                <form
                  onSubmit={onSignupSubmit}
                  className="signup_form w-full md:px-20 md:max-w-[500px]"
                >
                  <p>이메일</p>
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      name="email"
                      size="large"
                      value={signupFields.email}
                      onChange={handleSignupChange}
                      placeholder="이메일을 입력해주세요"
                      autoComplete="email"
                      required
                      className="flex-1"
                      readOnly={socialParam}
                    />
                    <button
                      type="button"
                      onClick={handleEmailCheck}
                      disabled={!validators.email(signupFields.email)}
                      className={`px-4 py-2 text-white border-none rounded-lg ${
                        !validators.email(signupFields.email)
                          ? "bg-[#bfbfbf] cursor-not-allowed opacity-80"
                          : emailCheckStatus.checked &&
                              emailCheckStatus.available
                            ? "bg-[#52c41a] cursor-pointer"
                            : "bg-[#1890ff] cursor-pointer"
                      }`}
                    >
                      {emailCheckStatus.checked
                        ? emailCheckStatus.available
                          ? "✓"
                          : "✗"
                        : "중복확인"}
                    </button>
                  </div>
                  <span className="text-xl text-gray-700 mt-2">
                    메일이 오지 않은 경우, 스팸함을 확인해주세요.
                  </span>
                  {emailCheckStatus.checked && (
                    <div
                      className={`text-xl mt-1 ${
                        emailCheckStatus.available
                          ? "text-[#52c41a]"
                          : "text-[#ff4d4f]"
                      }`}
                    >
                      {emailCheckStatus.available
                        ? "사용 가능한 이메일입니다."
                        : emailCheckStatus.message}
                    </div>
                  )}
                  {/* 실시간 이메일 검증 메시지 - 중복 확인 전에만 표시 */}
                  {!emailCheckStatus.checked &&
                    validationStatus.email.message && (
                      <div
                        className={`text-xl mt-1 ${
                          validationStatus.email.isValid
                            ? "text-[#52c41a]"
                            : "text-[#ff4d4f]"
                        }`}
                      >
                        {validationStatus.email.message}
                      </div>
                    )}
                  {/* 이메일 인증번호 입력 */}
                  {emailCheckStatus.checked && emailCheckStatus.available && (
                    <>
                      <p>이메일 인증번호</p>
                      <div className="flex gap-2">
                        <Input
                          type="text"
                          size="large"
                          value={emailVerification.code}
                          onChange={handleVerificationCodeChange}
                          placeholder="인증번호 6자리"
                          maxLength={6}
                          className="flex-1"
                          disabled={emailVerification.verified}
                        />
                        <button
                          type="button"
                          onClick={handleVerifyEmailCode}
                          disabled={
                            !emailVerification.code ||
                            emailVerification.code.length !== 6 ||
                            emailVerification.verified
                          }
                          className={`px-4 py-2 text-white border-none rounded-lg ${
                            !emailVerification.code ||
                            emailVerification.code.length !== 6
                              ? "bg-[#bfbfbf] cursor-not-allowed opacity-80"
                              : emailVerification.verified
                                ? "bg-[#52c41a] cursor-not-allowed"
                                : "bg-[#1890ff] cursor-pointer"
                          }`}
                        >
                          {emailVerification.verified ? "✓" : "인증하기"}
                        </button>
                      </div>
                      {emailVerification.sent &&
                        !emailVerification.verified && (
                          <div className="text-xl text-[#1890ff] mt-1 flex justify-between items-center">
                            <span>인증번호가 발송되었습니다.</span>
                            <span
                              className={
                                emailVerification.countdown < 60
                                  ? "text-[#ff4d4f]"
                                  : "text-[#666]"
                              }
                            >
                              {Math.floor(emailVerification.countdown / 60)}:
                              {(emailVerification.countdown % 60)
                                .toString()
                                .padStart(2, "0")}
                            </span>
                          </div>
                        )}
                      {emailVerification.verified && (
                        <div className="text-xl text-[#52c41a] mt-1">
                          ✓ 이메일 인증이 완료되었습니다.
                        </div>
                      )}
                    </>
                  )}
                  {/* 이메일 인증 완료 후에만 초대받은 회사 목록 표시 (모든 타입 공통) */}
                  {emailVerification.verified &&
                    filteredInvitedLogs.length > 0 && (
                      <>
                        <p>초대받은 회사 선택</p>
                        <div className="mb-4">
                          {filteredInvitedLogs.map((company) => (
                            <div
                              key={company.company_id}
                              className={`p-3 rounded-[8px] mb-2 cursor-pointer ${
                                selectedCompanyId === company.company_id
                                  ? "border-2 border-[#1890ff] bg-[#f0f8ff]"
                                  : "border border-[#d9d9d9] bg-white"
                              }`}
                              onClick={() => {
                                setSelectedCompanyId(company.company_id);
                                // 선택된 회사 정보로 폼 자동 채우기
                                if (
                                  signupType === "brand" ||
                                  signupType === "agency"
                                ) {
                                  setSignupFields((prev) => ({
                                    ...prev,
                                    company_name: company.company_name,
                                  }));
                                }
                              }}
                            >
                              <div className="font-bold mb-1">
                                {company.company_name}
                              </div>
                            </div>
                          ))}
                          <div className="text-xl text-[#666] mt-2">
                            * 회사를 선택하면 회사 정보가 자동으로 입력됩니다
                          </div>
                        </div>
                      </>
                    )}

                  {/* 소셜 로그인이 아닐 때만 비밀번호 필드 표시 */}
                  {!socialParam && (
                    <>
                      <p>비밀번호</p>
                      <Input.Password
                        size="large"
                        name="password"
                        value={signupFields.password}
                        onChange={handleSignupChange}
                        placeholder="비밀번호를 입력하세요"
                        autoComplete="new-password"
                        required
                      />

                      <div className="text-xl text-[#666] mt-1">
                        최소 8자 이상, 대/소문자, 숫자, 특수문자(@$!%*?&)를
                        포함해야 합니다
                      </div>
                      {validationStatus.password.message && (
                        <div
                          className={`text-xl mt-1 ${
                            validationStatus.password.isValid
                              ? "text-[#52c41a]"
                              : "text-[#ff4d4f]"
                          }`}
                        >
                          {validationStatus.password.message}
                        </div>
                      )}
                      <p>비밀번호 확인</p>
                      <Input.Password
                        size="large"
                        name="passwordCheck"
                        value={signupFields.passwordCheck}
                        onChange={handleSignupChange}
                        placeholder="비밀번호를 다시 입력하세요"
                        autoComplete="new-password"
                        required
                      />
                      {validationStatus.passwordCheck.message && (
                        <div
                          className={`text-xl mt-1 ${
                            validationStatus.passwordCheck.isValid
                              ? "text-[#52c41a]"
                              : "text-[#ff4d4f]"
                          }`}
                        >
                          {validationStatus.passwordCheck.message}
                        </div>
                      )}
                    </>
                  )}
                  {signupType === "influencer" && (
                    <>
                      <p>닉네임</p>
                      <Input
                        size="large"
                        type="text"
                        name="nickname"
                        value={signupFields.nickname}
                        onChange={handleSignupChange}
                        placeholder="닉네임을 입력해주세요"
                        required
                      />
                    </>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <p>이름</p>
                      <Input
                        type="text"
                        size="large"
                        name="first_name"
                        value={signupFields.first_name}
                        onChange={handleSignupChange}
                        placeholder="이름을 입력해주세요"
                        required
                        readOnly={socialParam}
                      />
                      {validationStatus.first_name.message && (
                        <div
                          className={`text-xl mt-1 ${
                            validationStatus.first_name.isValid
                              ? "text-[#52c41a]"
                              : "text-[#ff4d4f]"
                          }`}
                        >
                          {validationStatus.first_name.message}
                        </div>
                      )}
                    </div>
                    <div>
                      <p>성</p>
                      <Input
                        type="text"
                        size="large"
                        name="last_name"
                        value={signupFields.last_name}
                        onChange={handleSignupChange}
                        placeholder="성을 입력해주세요"
                        required
                        readOnly={socialParam}
                      />
                      {validationStatus.last_name.message && (
                        <div
                          className={`text-xl mt-1 ${
                            validationStatus.last_name.isValid
                              ? "text-[#52c41a]"
                              : "text-[#ff4d4f]"
                          }`}
                        >
                          {validationStatus.last_name.message}
                        </div>
                      )}
                    </div>
                  </div>
                  <p>전화번호</p>
                  <PhoneInput
                    international
                    defaultCountry="KR"
                    value={signupFields.phone}
                    onChange={(value) => {
                      setSignupFields((prev) => ({
                        ...prev,
                        phone: value || "",
                      }));
                      const validation = validateField("phone", value || "");
                      setValidationStatus((prev) => ({
                        ...prev,
                        phone: validation,
                      }));
                    }}
                    placeholder="전화번호를 입력해주세요"
                    required
                    aria-required="true"
                  />
                  {validationStatus.phone.message && (
                    <div
                      className={`text-xl mt-1 ${
                        validationStatus.phone.isValid
                          ? "text-[#52c41a]"
                          : "text-[#ff4d4f]"
                      }`}
                    >
                      {validationStatus.phone.message}
                    </div>
                  )}
                  <p>국가</p>
                  <Select
                    size="large"
                    placeholder="국가를 선택해주세요"
                    value={signupFields.country || undefined}
                    onChange={(value) =>
                      handleSignupChange({
                        target: { name: "country", value: value || "" },
                      })
                    }
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    {Country.getAllCountries().map((country) => (
                      <Select.Option
                        key={country.isoCode}
                        value={country.isoCode}
                        label={country.name}
                      >
                        {country.name} ({country.isoCode})
                      </Select.Option>
                    ))}
                  </Select>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <p>시/도</p>
                      <Input
                        size="large"
                        type="text"
                        name="state_province"
                        value={signupFields.state_province}
                        onChange={handleSignupChange}
                        placeholder="시/도를 입력해주세요 (예: 서울특별시, 경기도)"
                        required
                      />
                    </div>
                    <div>
                      <p>시/군/구</p>
                      <Input
                        size="large"
                        type="text"
                        name="city"
                        value={signupFields.city}
                        onChange={handleSignupChange}
                        placeholder="시/군/구를 입력해주세요 (예: 마포구, 강남구)"
                        required
                      />
                    </div>
                  </div>
                  <p>도로명 주소</p>
                  <Input
                    size="large"
                    type="text"
                    name="street_address"
                    value={signupFields.street_address}
                    onChange={handleSignupChange}
                    placeholder="도로명 주소를 입력해주세요 (예: 마포대로 123)"
                    required
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <p>상세주소</p>
                      <Input
                        size="large"
                        type="text"
                        name="address_line"
                        value={signupFields.address_line}
                        onChange={handleSignupChange}
                        placeholder="상세주소를 입력해주세요 (예: 래미안아파트 101동 1504호)"
                        required
                      />
                    </div>
                    <div>
                      <p>우편번호</p>
                      <Input
                        size="large"
                        type="text"
                        name="postal_code"
                        value={signupFields.postal_code}
                        onChange={handleSignupChange}
                        placeholder="우편번호를 입력해주세요"
                        required
                      />
                    </div>
                  </div>
                  {signupType === "influencer" && (
                    <div className="grid grid-cols-[3fr_7fr] md:grid-cols-2 gap-2">
                      <div>
                        <p>성별</p>
                        <Select
                          size="large"
                          placeholder="성별을 선택해주세요"
                          value={signupFields.gender || undefined}
                          onChange={(value) =>
                            handleSignupChange({
                              target: { name: "gender", value: value || "" },
                            })
                          }
                          className="w-full"
                          required
                        >
                          <Select.Option value="female">여성</Select.Option>
                          <Select.Option value="male">남성</Select.Option>
                          <Select.Option value="unknown">기타</Select.Option>
                        </Select>
                      </div>
                      <div className="min-w-0">
                        <p>생년월일</p>
                        <DatePicker
                          size="large"
                          className="w-full min-w-0"
                          placeholder="생년월일을 선택해주세요"
                          format="YYYY-MM-DD"
                          value={
                            signupFields.birth
                              ? dayjs(signupFields.birth, "YYYY-MM-DD")
                              : null
                          }
                          onChange={(date, dateString) => {
                            handleSignupChange({
                              target: {
                                name: "birth",
                                value: dateString || "",
                              },
                            });
                          }}
                          popupStyle={{
                            zIndex: 1050,
                          }}
                          required
                        />
                      </div>
                    </div>
                  )}

                  {(signupType === "brand" || signupType === "agency") && (
                    <>
                      {/* 초대받은 회사를 선택했는지 확인 */}
                      {emailVerification.verified &&
                      invitedLogs.some(
                        (log) => log.invite_type === signupType
                      ) &&
                      selectedCompanyId ? (
                        // 초대받은 회사를 선택했으면 모든 필드 숨김 (기존 로직)
                        <></>
                      ) : (
                        // 초대받은 회사를 선택하지 않았으면 모든 필드 표시 (grid 사용)
                        <>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <p>
                                {signupType === "brand"
                                  ? "브랜드명"
                                  : "에이전시명"}
                              </p>
                              <Input
                                size="large"
                                type="text"
                                name="company_name"
                                value={signupFields.company_name}
                                onChange={handleSignupChange}
                                placeholder={
                                  signupType === "brand"
                                    ? "브랜드명을 입력해주세요"
                                    : "에이전시명을 입력해주세요"
                                }
                                required
                              />
                              {validationStatus.company_name?.message && (
                                <div
                                  className={`text-xl mt-1 ${
                                    validationStatus.company_name.isValid
                                      ? "text-[#52c41a]"
                                      : "text-[#ff4d4f]"
                                  }`}
                                >
                                  {validationStatus.company_name.message}
                                </div>
                              )}
                            </div>
                            <div>
                              <p>업종</p>
                              <Select
                                size="large"
                                placeholder="업종을 선택해주세요"
                                value={signupFields.industry || undefined}
                                onChange={(value) =>
                                  handleSignupChange({
                                    target: {
                                      name: "industry",
                                      value: value || "",
                                    },
                                  })
                                }
                                className="w-full"
                              >
                                {Object.entries(CATEGORY_MAPPING).map(
                                  ([key, label]) => (
                                    <Select.Option key={key} value={key}>
                                      {label}
                                    </Select.Option>
                                  )
                                )}
                              </Select>
                            </div>
                          </div>
                          <p>사업자등록번호</p>
                          <div className="flex gap-2">
                            <Input
                              size="large"
                              type="text"
                              name="registration_number"
                              value={signupFields.registration_number}
                              onChange={(e) => {
                                const value = e.target.value;

                                const nextValue = isKorea
                                  ? value.replace(/[^0-9]/g, "")
                                  : value.slice(0, 50);

                                setRegistrationNumberCheckStatus({
                                  available: false,
                                  message: "",
                                });
                                handleSignupChange({
                                  target: {
                                    name: "registration_number",
                                    value: nextValue,
                                  },
                                });
                              }}
                              placeholder="사업자등록번호를 입력해주세요"
                              className="flex-1"
                              required
                            />
                            {isKorea ? (
                              <button
                                type="button"
                                onClick={handleRegistrationNumberCheck}
                                disabled={
                                  registrationNumberCheckStatus?.available ||
                                  !signupFields.registration_number
                                }
                                className={`px-4 py-2 text-white bg-[#1890ff] border-none rounded-lg ${
                                  registrationNumberCheckStatus?.available ||
                                  !signupFields.registration_number
                                    ? "bg-[#bfbfbf] cursor-not-allowed opacity-80"
                                    : "bg-[#1890ff] cursor-pointer"
                                }`}
                              >
                                인증하기
                              </button>
                            ) : null}
                          </div>
                          {registrationNumberCheckStatus?.available && (
                            <div className="text-xl mt-1 text-[#52c41a]">
                              {registrationNumberCheckStatus.message}
                            </div>
                          )}
                          {!registrationNumberCheckStatus?.available && (
                            <div className="text-xl mt-1 text-[#ff4d4f]">
                              {registrationNumberCheckStatus.message}
                            </div>
                          )}

                          <p>사업자등록증</p>
                          <span className="text-lg -mt-4 mb-4 text-[#666]">
                            사업자 등록증 또는 W-9/Invoice 이미지로
                            업로드해주세요.
                          </span>
                          <div className="flex gap-4 items-start">
                            <Upload
                              listType="picture-card"
                              beforeUpload={handleFileSelect}
                              accept="image/*"
                              showUploadList={false}
                            >
                              <div>
                                <UploadOutlined />
                                <div>업로드</div>
                              </div>
                            </Upload>

                            {(selectedFile || registrationImg) && (
                              <div>
                                <Image
                                  src={
                                    selectedFile
                                      ? URL.createObjectURL(selectedFile)
                                      : registrationImg
                                  }
                                  alt="사업자 등록증"
                                  width={102}
                                  height={102}
                                  className="rounded-lg object-cover border border-[#d9d9d9] cursor-pointer"
                                  preview={{
                                    mask: false,
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </>
                  )}
                  <div className="mt-12 mb-6 space-y-6">
                    <div className="grid grid-cols-[1fr_auto] items-center gap-4">
                      <Checkbox
                        name="terms_agreement"
                        checked={signupFields.terms_agreement}
                        onChange={(e) => {
                          handleSignupChange({
                            target: {
                              name: e.target.name,
                              type: "checkbox",
                              checked: e.target.checked,
                            },
                          });
                        }}
                        required
                      >
                        서비스 이용 약관 (필수)
                      </Checkbox>
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(
                            "/docs/terms",
                            "이용약관",
                            "width=650, height=900, scrollbars=yes, resizable=yes"
                          );
                        }}
                        className="text-[#1890ff] underline text-xl cursor-pointer whitespace-nowrap"
                      >
                        보기
                      </span>
                    </div>
                    <div className="grid grid-cols-[1fr_auto] items-center gap-4">
                      <Checkbox
                        name="private_agreement"
                        checked={signupFields.private_agreement}
                        onChange={(e) => {
                          handleSignupChange({
                            target: {
                              name: e.target.name,
                              type: "checkbox",
                              checked: e.target.checked,
                            },
                          });
                        }}
                        required
                      >
                        개인정보처리방침 동의 (필수)
                      </Checkbox>
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(
                            "/docs/privacy",
                            "개인정보처리방침",
                            "width=650, height=900, scrollbars=yes, resizable=yes"
                          );
                        }}
                        className="text-[#1890ff] underline text-xl cursor-pointer whitespace-nowrap"
                      >
                        보기
                      </span>
                    </div>
                    <div className="grid grid-cols-[1fr_auto] items-center gap-4">
                      <Checkbox
                        name="marketing_agreement"
                        checked={signupFields.marketing_agreement}
                        onChange={(e) => {
                          handleSignupChange({
                            target: {
                              name: e.target.name,
                              type: "checkbox",
                              checked: e.target.checked,
                            },
                          });
                        }}
                      >
                        마케팅 정보 수신 (선택)
                      </Checkbox>
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(
                            "/docs/marketing",
                            "마케팅 수신 동의",
                            "width=650, height=900, scrollbars=yes, resizable=yes"
                          );
                        }}
                        className="text-[#1890ff] underline text-xl cursor-pointer whitespace-nowrap"
                      >
                        보기
                      </span>
                    </div>
                  </div>
                  <button type="submit" className="btn">
                    {signupType === "brand"
                      ? "브랜드 가입하기"
                      : signupType === "agency"
                        ? "에이전시 가입하기"
                        : "크리에이터 가입하기"}
                  </button>
                </form>
              </>
            )}
            {signupType && (
              <button
                type="button"
                className="signup_btn"
                onClick={() => {
                  history.push("/signin");
                  setSignupType(false);
                }}
              >
                로그인 페이지로 돌아가기
              </button>
            )}
          </div>
          <div className="intro hidden md:block flex-1"></div>
        </>
      )}
    </div>
  );
};
